/**
 * DiscussionAutoScoreModal — Phase 1.7d
 * Faculty picks a discussion thread (scoped to the rubric's module/topic)
 * and runs AI scoring on every participant's posts in one click.
 */
import React, { useEffect, useState } from 'react';
import { listDiscussionThreads, autoScoreDiscussion } from '../lib/apiClient';

const DiscussionAutoScoreModal = ({ rubric, onClose, onCompleted }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState('');
  const [excludeFaculty, setExcludeFaculty] = useState(true);
  const [runAi, setRunAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    const params = {};
    if (rubric.module_id) params.module_id = rubric.module_id;
    if (rubric.topic_id) params.topic_id = rubric.topic_id;
    listDiscussionThreads(params)
      .then((t) => {
        setThreads(t);
        if (t.length) setPicked(t[0].id);
      })
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load threads'))
      .finally(() => setLoading(false));
  }, [rubric.module_id, rubric.topic_id]);

  const run = async () => {
    if (!picked) return;
    setBusy(true); setErr('');
    try {
      const r = await autoScoreDiscussion(picked, {
        rubric_id: rubric.id,
        exclude_faculty: excludeFaculty,
        run_ai_score: runAi,
      });
      setResult(r);
      onCompleted && onCompleted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Auto-score failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="auto-score-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Phase 1.7d</div>
            <div className="text-lg font-bold text-gray-900">Auto-score a discussion</div>
            <div className="text-xs text-gray-500 mt-0.5">Rubric: {rubric.title}</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-2xl leading-none" data-testid="auto-score-close-btn">×</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && <div className="text-gray-500">Loading threads…</div>}
          {!loading && threads.length === 0 && (
            <div className="text-gray-500" data-testid="auto-score-no-threads">
              No discussion threads found {rubric.module_id ? `for module ${rubric.module_id}` : ''}.
              Create one from the module page, then re-open this dialog.
            </div>
          )}
          {!loading && threads.length > 0 && !result && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Thread</label>
                <select
                  className="input-field w-full" value={picked}
                  onChange={(e) => setPicked(e.target.value)}
                  data-testid="auto-score-thread-select"
                >
                  {threads.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title} — {(t.replies || []).length + 1} posts
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={excludeFaculty} onChange={(e) => setExcludeFaculty(e.target.checked)} data-testid="auto-score-exclude-faculty" />
                Exclude faculty posts (don't grade developer/teacher/admin)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={runAi} onChange={(e) => setRunAi(e.target.checked)} data-testid="auto-score-run-ai" />
                Run AI scoring immediately after creating submissions
              </label>
              {err && <div className="p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}
              <button onClick={run} disabled={busy} className="btn-primary w-full" data-testid="auto-score-run-btn">
                {busy ? 'Scoring…' : 'Create submissions + score'}
              </button>
            </div>
          )}
          {result && (
            <div data-testid="auto-score-result">
              <div className="p-3 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm mb-3">
                ✓ Created {result.participants} submission(s){result.ai_scored ? ' and ran AI scoring' : ''}.
              </div>
              <ul className="space-y-1 text-sm">
                {result.submissions.map((s) => (
                  <li key={s.submission_id} className="flex justify-between border-b py-1">
                    <span>{s.student_name || s.student_email}</span>
                    <span className="text-gray-500">{s.post_count} post(s)</span>
                  </li>
                ))}
              </ul>
              <button onClick={onClose} className="btn-secondary mt-4 w-full" data-testid="auto-score-done-btn">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussionAutoScoreModal;
